/**
 * 华为盯盘策略 · 脑图数据构建器（条件前缀树 → 执行操作）。
 *
 * 为什么：策略配置页策略条数增多后，五列表格需长时间滚动，无法整体把握；
 * 脑图把「同一层中内容完全相同的结点」合并为一个（标注「启用 x/共 y」），
 * 形成 全部策略 → 条件1 → 条件2 → … → 执行操作 的前缀树，供整体预览与数值编辑。
 *
 * 合并规则：
 *   1. 每条策略的条件组先按 compareConditions 固定排序（属性指标优先——siteset 最前，
 *      使同任务类型策略聚拢；数值指标按 时间窗 → 指标 → 操作符 → 阈值模式 → 阈值 全序），
 *      再逐层 find-or-create 插入前缀树，公共前缀天然合并；
 *   2. 条件合并键用原始字段（metric|period|operator|value_mode|value），不含目标费率/
 *      成本基线——基线变化只影响展示文案（label），不影响树结构；
 *   3. 操作叶子合并键为 action_type|action_value；完全相同（条件组+操作）的策略
 *      合并为同一叶子，条数计入 total/enabledCount；
 *   4. 边界：[A] 与 [A,B] 真前缀 → A 结点的子级 = 操作叶子（归属 [A]）+ 条件结点 B 混排；
 *      空条件组不可能（后端/编辑器 min 1），仍防御性跳过。
 *
 * 计数口径：每个结点 total/enabledCount = 子树内全部策略（插入时逐层累加），
 * 停用策略参与合并；组件侧对 enabledCount === 0 的结点做弱化展示。
 *
 * 数值内联编辑（PR-HW-20261102-001）：数值型条件结点与带参操作叶子携带 editable
 * 拆段（prefix/valueText/suffix，拼接 === label）；编辑语义 = 批量应用到途经/归属
 * 该结点的全部策略，指标名称/操作符/周期/动作类型不可编辑。校验与 PUT body 组装
 * 由本文件导出的 validateNodeValue / build*PatchBodies 承担（merge-key 语义内聚）。
 *
 * 本文件为纯函数 view-model 构建器（不 import echarts / 颜色概念），与
 * WatchStrategyEditor.tsx 同目录内聚；类型 MindMapNode 为纯前端视图模型，
 * 不进 types/（该目录对齐后端 Pydantic DTO）。
 */

import {
  type ConditionItem,
  type WatchStrategyRecord,
  type SaveWatchStrategyBody,
  PERIOD_OPTIONS,
  ACTION_TYPE_OPTIONS,
  describeCondition,
  describeConditionParts,
  describeAction,
  describeActionParts,
} from "../../services/huawei/watchStrategy";

/** 脑图结点类别：root=根；condition=条件结点；action=执行操作叶子。 */
export type MindMapNodeKind = "root" | "condition" | "action";

/**
 * 脑图结点可编辑数值描述（仅数值型条件结点与带参操作叶子持有）。
 * 约束：prefix + valueText + suffix === label，结点宽度测量仍按整 label 估算。
 * 编辑语义：结点为同条件合并的前缀树节点，修改数值 = 批量应用到途经/归属该结点的
 * 全部策略（node.strategies）。
 */
export interface MindMapEditableValue {
  /** condition_value=条件阈值；action_value=操作数值。 */
  kind: "condition_value" | "action_value";
  /** 只读前缀（周期·指标名·操作符·「目标费率×」/动作名等）。 */
  prefix: string;
  /** 只读后缀（倍率基线生效值「(=0.012)」/「%」）。 */
  suffix: string;
  /** 当前数值展示文本（内联编辑初始值；倍率模式为倍数 x，调/提预算为百分比数字）。 */
  valueText: string;
}

/** 操作徽章语义色（镜像 StrategyConfigPage.watchActionTagClass 的口径）。 */
export type ActionTone = "danger" | "warning" | "info";

/** 脑图结点（echarts tree data 的语义源；组件侧再映射为图形属性）。 */
export interface MindMapNode {
  /** 合并键：root="root"；条件=规范化条件键；操作=`${action_type}|${action_value}`。 */
  key: string;
  kind: MindMapNodeKind;
  /** 展示文案（describeCondition / describeAction 产出，倍率模式带基线生效值）。 */
  label: string;
  /** 仅条件结点持有（供子结点定序）。 */
  condition?: ConditionItem;
  /** 仅操作叶子持有（供叶子间定序与 tooltip）。 */
  actionType?: WatchStrategyRecord["action_type"];
  actionValue?: number;
  /** 仅操作叶子持有：语义色基调。 */
  actionTone?: ActionTone;
  /** 可编辑数值（数值型条件结点 / 带参操作叶子；枚举条件与暂停·预警叶子无此字段）。 */
  editable?: MindMapEditableValue;
  /** 子树内策略总数（含停用）。 */
  total: number;
  /** 子树内启用策略数。 */
  enabledCount: number;
  /** 途经（条件结点）/ 归属（操作叶子）的策略，finalize 后按 id 升序。 */
  strategies: WatchStrategyRecord[];
  children: MindMapNode[];
}

/** 属性指标排序 rank（siteset 最前 → 同任务类型策略聚拢；未知属性兜底 9）。 */
const ATTRIBUTE_METRIC_RANK: Record<string, number> = {
  siteset: 0,
  is_work_day: 1,
  present_day: 2,
  present_hour: 3,
};

const PERIOD_ORDER = new Map(PERIOD_OPTIONS.map((o, i) => [o.value, i]));
const ACTION_ORDER = new Map(ACTION_TYPE_OPTIONS.map((o, i) => [o.value, i]));

/** 原始值比较：双 number 用数值比较（避免 "10" < "2" 字典序陷阱），否则 String 字典序
 * （多值枚举 value 为 string[]，String 化为 "14,15" 参与字典序，确定性即可）。 */
function comparePrimitive(
  a: number | string | readonly string[],
  b: number | string | readonly string[],
): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  const sa = String(a);
  const sb = String(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

/**
 * 条件排序比较器（组内 AND 无序 → 固定全序，保证相同条件集合收敛到同一前缀路径）：
 * 属性指标在前（按 ATTRIBUTE_METRIC_RANK）；数值指标在后，按 PERIOD_OPTIONS 顺序
 * → metric → operator → value_mode → value 全序。
 */
export function compareConditions(a: ConditionItem, b: ConditionItem): number {
  const aAttr = a.metric_type === "attribute";
  const bAttr = b.metric_type === "attribute";
  if (aAttr !== bAttr) return aAttr ? -1 : 1;
  if (aAttr && bAttr) {
    const ra = ATTRIBUTE_METRIC_RANK[a.metric] ?? 9;
    const rb = ATTRIBUTE_METRIC_RANK[b.metric] ?? 9;
    if (ra !== rb) return ra - rb;
  } else {
    const pa = PERIOD_ORDER.get(a.period ?? "") ?? 99;
    const pb = PERIOD_ORDER.get(b.period ?? "") ?? 99;
    if (pa !== pb) return pa - pb;
  }
  if (a.metric !== b.metric) return a.metric < b.metric ? -1 : 1;
  if (a.operator !== b.operator) return a.operator < b.operator ? -1 : 1;
  const am = a.value_mode ?? "fixed";
  const bm = b.value_mode ?? "fixed";
  if (am !== bm) return am < bm ? -1 : 1;
  return comparePrimitive(a.value, b.value);
}

/** 条件合并键（原始字段规范化；value_mode 缺省按 fixed 口径，与后端一致）。 */
function conditionKey(c: ConditionItem): string {
  return `${c.metric}|${c.period ?? ""}|${c.operator}|${c.value_mode ?? "fixed"}|${String(c.value)}`;
}

/** 操作语义色基调（与表格徽章一致：暂停=红，降预算/预警=黄，其余=青 info）。 */
function actionToneOf(actionType: string): ActionTone {
  if (actionType === "stop_adgroup") return "danger";
  if (actionType === "reduce_budget" || actionType === "warning") return "warning";
  return "info";
}

/** 子结点定序：操作叶子恒排前（语义=「此前缀的完整策略」），条件结点在后按比较器排。 */
function compareNodes(a: MindMapNode, b: MindMapNode): number {
  if (a.kind !== b.kind) return a.kind === "action" ? -1 : 1;
  if (a.kind === "action" && b.kind === "action") {
    const ra = ACTION_ORDER.get(a.actionType ?? "") ?? 99;
    const rb = ACTION_ORDER.get(b.actionType ?? "") ?? 99;
    if (ra !== rb) return ra - rb;
    return comparePrimitive(a.actionValue ?? 0, b.actionValue ?? 0);
  }
  if (a.condition && b.condition) return compareConditions(a.condition, b.condition);
  return 0;
}

/** 递归定序子结点 + 各结点 strategies 按 id 升序（后端列表为 id 倒序，tooltip 展示升序更自然）。 */
function finalize(node: MindMapNode): void {
  node.strategies.sort((a, b) => a.id - b.id);
  node.children.sort(compareNodes);
  for (const child of node.children) finalize(child);
}

/**
 * 构建脑图前缀树。
 *
 * 入参：
 *   strategies — 全量策略（页面已一次性拉取 ≤200 条）；
 *   opts.targetSxfl / targetCost — 目标费率 / 目标成本基线（仅影响倍率模式条件的
 *     展示文案「(=生效阈值)」，不参与合并键）。
 *
 * 返回：根结点（kind="root"，label="全部策略"）；空入参返回 total=0 的空根。
 */
export function buildStrategyMindMap(
  strategies: WatchStrategyRecord[],
  opts: { targetSxfl?: number; targetCost?: number },
): MindMapNode {
  const root: MindMapNode = {
    key: "root",
    kind: "root",
    label: "全部策略",
    total: 0,
    enabledCount: 0,
    strategies: [],
    children: [],
  };

  for (const s of strategies) {
    if (!s.conditions || s.conditions.length === 0) continue; // 防御：条件组下限 1
    const sorted = [...s.conditions].sort(compareConditions);

    // 途经结点逐层累加计数（天然保证「每层结点 = 子树内全部策略」口径）。
    root.total++;
    if (s.is_enabled) root.enabledCount++;
    root.strategies.push(s);

    let node = root;
    for (const c of sorted) {
      const key = conditionKey(c);
      let child = node.children.find((it) => it.kind === "condition" && it.key === key);
      if (!child) {
        // 数值型条件（含倍率模式）支持脑图内联编辑；枚举条件（value 为 string/string[]）不可编辑
        const parts = describeConditionParts(c, opts.targetSxfl, opts.targetCost);
        child = {
          key,
          kind: "condition",
          label: describeCondition(c, opts.targetSxfl, opts.targetCost),
          condition: c,
          editable: parts ? { kind: "condition_value", ...parts } : undefined,
          total: 0,
          enabledCount: 0,
          strategies: [],
          children: [],
        };
        node.children.push(child);
      }
      child.total++;
      if (s.is_enabled) child.enabledCount++;
      child.strategies.push(s);
      node = child;
    }

    // 末端追加操作叶子（key = action_type|action_value；完全相同策略合并为同一叶子）。
    const akey = `${s.action_type}|${s.action_value ?? ""}`;
    let leaf = node.children.find((it) => it.kind === "action" && it.key === akey);
    if (!leaf) {
      // 带数值参数的操作（预算降低/提升/调整至）支持脑图内联编辑；暂停投放/预警无数值
      const actionParts = describeActionParts(s.action_type, s.action_value);
      leaf = {
        key: akey,
        kind: "action",
        label: describeAction(s.action_type, s.action_value),
        actionType: s.action_type,
        actionValue: s.action_value,
        actionTone: actionToneOf(s.action_type),
        editable: actionParts ? { kind: "action_value", ...actionParts } : undefined,
        total: 0,
        enabledCount: 0,
        strategies: [],
        children: [],
      };
      node.children.push(leaf);
    }
    leaf.total++;
    if (s.is_enabled) leaf.enabledCount++;
    leaf.strategies.push(s);
  }

  finalize(root);
  return root;
}

/* ---------- 数值内联编辑（校验 + 批量 PUT body 组装） ---------- */

/**
 * 脑图内联编辑数值校验（口径镜像 WatchStrategyEditor.buildBody）。
 *
 * @returns 错误文案（toast 用）或 null（合法）。
 */
export function validateNodeValue(node: MindMapNode, v: number): string | null {
  const ed = node.editable;
  if (!ed) return "该节点不支持数值编辑";
  if (!Number.isFinite(v)) return "数值须为可解析数字";
  if (ed.kind === "condition_value") {
    // 条件阈值：固定值模式仅需可解析（同编辑器口径，无上下界）；倍率模式倍数 x 须为正数
    const mode = node.condition?.value_mode ?? "fixed";
    if (mode !== "fixed" && v <= 0) return "倍率模式的倍数 x 须为正数";
    return null;
  }
  if (node.actionType === "reduce_budget" || node.actionType === "increase_budget") {
    if (!(v > 0 && v < 100)) {
      return "调/提价百分比须为 (0,100) 开区间数字（如 10 表示 10%）";
    }
    return null;
  }
  if (node.actionType === "set_budget") {
    if (!(v > 0)) return "预算调整至的目标金额须为正数（元）";
    return null;
  }
  return "该操作不支持数值编辑";
}

/**
 * 条件结点数值编辑 → 批量 PUT body：途经策略中合并键等于本结点 key 的条件替换阈值，
 * 其余条件字段与操作原样（key 含旧值，按结点保存的 key 匹配；同策略存在重复条件时
 * 一并替换，语义与「该前缀条件」一致）。
 */
export function buildConditionValuePatchBodies(
  node: MindMapNode,
  newValue: number,
): { id: number; body: SaveWatchStrategyBody }[] {
  return node.strategies.map((s) => ({
    id: s.id,
    body: {
      conditions: s.conditions.map((c) =>
        conditionKey(c) === node.key ? { ...c, value: newValue } : c,
      ),
      action_type: s.action_type,
      action_value: s.action_value ?? null,
    },
  }));
}

/**
 * 操作叶子数值编辑 → 批量 PUT body：入参为展示口径数值（调/提预算 = 百分比数字
 * 如 10，本函数换算为存储小数 0.10；set_budget = 目标金额元直传），条件组原样。
 */
export function buildActionValuePatchBodies(
  node: MindMapNode,
  newValue: number,
): { id: number; body: SaveWatchStrategyBody }[] {
  const stored =
    node.actionType === "reduce_budget" || node.actionType === "increase_budget"
      ? newValue / 100
      : newValue;
  return node.strategies.map((s) => ({
    id: s.id,
    body: {
      conditions: s.conditions,
      action_type: s.action_type,
      action_value: stored,
    },
  }));
}
